import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { api, ApiRequestError } from "../../api";
import { authStore, useAuth } from "../../auth/store";
import { PasskeyCancelledError, getPasskey, passkeysSupported } from "../../auth/webauthn";
import { Turnstile, turnstileSiteKey } from "../../auth/Turnstile";
import { BusyButton, Field } from "../../components/ui";
import { AuthFrame } from "./LoginPage";
import { passwordProblems } from "./SignupPage";

// ---------------------------------------------------------------- Verify email
const resendWait = 30;

function retrySeconds(e: unknown): number | null {
  const body = (e as ApiRequestError).body;
  if (!body || typeof body !== "object" || !("retryAfter" in body)) return null;
  const n = (body as { retryAfter?: unknown }).retryAfter;
  return typeof n === "number" && n > 0 ? Math.ceil(n) : null;
}

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string };
  const auth = useAuth();
  const [state, setState] = useState<"idle" | "verifying" | "done" | "error">(search.token ? "verifying" : "idle");
  const [error, setError] = useState<string | null>(null);
  const [resend, setResend] = useState<"idle" | "sending" | "sent">("idle");
  // Opening this page means a link just went out, so the next one waits.
  const [wait, setWait] = useState(search.token ? 0 : resendWait);
  useEffect(() => {
    if (wait <= 0) return;
    const id = window.setTimeout(() => setWait((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [wait]);
  const resendLink = async () => {
    if (wait > 0) return;
    setResend("sending");
    setError(null);
    try {
      await api.auth.resendVerifyEmail();
      setResend("sent");
      setWait(resendWait);
    } catch (e) {
      const retry = retrySeconds(e);
      if (retry != null) {
        setWait(retry);
        setResend("idle");
      } else {
        setError((e as ApiRequestError).message);
        setResend("idle");
      }
    }
  };
  useEffect(() => {
    if (!search.token) return;
    api.auth
      .verifyEmail(search.token)
      .then((r) => {
        authStore.applyAuthResult(r);
        authStore.clearNext("verify_email");
        setState("done");
        setTimeout(() => {
          if (r.planIntent && r.orgs[0]) {
            void navigate({
              to: "/orgs/$org/billing",
              params: { org: r.orgs[0].id },
              search: { plan: r.planIntent },
            });
          } else {
            void navigate({ to: "/" });
          }
        }, 800);
      })
      .catch((e: ApiRequestError) => {
        setError(e.message);
        setState("error");
      });
  }, [search.token, navigate]);
  if (state === "verifying") return <AuthFrame title="Verifying…" lead="One moment." />;
  if (state === "done") return <AuthFrame title="Email verified" lead="Taking you to your sessions." />;
  return (
    <AuthFrame title="Verify your email" lead={`We sent a verification link to ${auth.me?.user.email ?? "your address"}. Open it to continue.`}>
      {error && (
        <div className="error-box" role="alert">
          {error}
        </div>
      )}
      {resend === "sent" && (
        <p className="muted" role="status">
          A new link is on its way. Check your inbox and spam folder.
        </p>
      )}
      <div className="auth-links">
        <BusyButton className="btn ghost sm" type="button" busy={resend === "sending"} busyLabel="Sending…" disabled={wait > 0} onClick={() => void resendLink()}>
          {wait > 0 ? `Resend link in ${wait}s` : "Resend link"}
        </BusyButton>
        <Link to="/login">Back to sign in</Link>
      </div>
    </AuthFrame>
  );
}

// ---------------------------------------------------------------- Magic link consume
export function MagicLinkPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string; next?: string };
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!search.token) return;
    api.auth
      .magicLinkConsume(search.token)
      .then((r) => {
        authStore.applyAuthResult(r);
        void navigate({ to: (search.next && search.next.startsWith("/") ? search.next : "/") as "/" });
      })
      .catch((e: ApiRequestError) => setError(e.message));
  }, [search.token, search.next, navigate]);
  return (
    <AuthFrame title={error ? "Link expired" : "Signing you in…"} lead={error ?? undefined}>
      {error && <Link to="/login">Request a new link</Link>}
    </AuthFrame>
  );
}

// ---------------------------------------------------------------- Forgot / Reset
export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileTry, setTurnstileTry] = useState(0);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.auth.forgot(email, turnstileToken);
      setSent(true);
    } catch (err) {
      setError((err as ApiRequestError).message);
      setTurnstileToken("");
      setTurnstileTry((n) => n + 1);
    }
  };
  if (sent) return <AuthFrame title="Check your email" lead="If an account exists for that address, a reset link is on its way." />;
  return (
    <AuthFrame title="Reset your password">
      <form onSubmit={submit} autoComplete="off">
        <Field label="Email">{(p) => <input {...p} className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />}</Field>
        <Turnstile key={turnstileTry} onToken={setTurnstileToken} />
        {error && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}
        <button className="btn primary block" type="submit" disabled={!email || (!!turnstileSiteKey() && !turnstileToken)}>
          Send reset link
        </button>
      </form>
      <div className="auth-links">
        <Link to="/login">Back to sign in</Link>
      </div>
    </AuthFrame>
  );
}

export function ResetPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string };
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const pwErr = touched ? passwordProblems(password) : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (passwordProblems(password)) return;
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    try {
      await api.auth.reset(search.token ?? "", password);
      void navigate({ to: "/login", search: { msg: "Password updated. Sign in with your new password." } });
    } catch (err) {
      setError((err as ApiRequestError).message);
    }
  };
  return (
    <AuthFrame title="Choose a new password">
      <form onSubmit={submit} noValidate autoComplete="off">
        <Field label="New password" error={pwErr}>
          {(p) => <input {...p} className="input" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} onBlur={() => setTouched(true)} />}
        </Field>
        <Field label="Confirm" error={error}>
          {(p) => <input {...p} className="input" type="password" autoComplete="off" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
        </Field>
        <button className="btn primary block" type="submit" disabled={!password || !confirm}>
          Update password
        </button>
      </form>
    </AuthFrame>
  );
}

// ---------------------------------------------------------------- MFA challenge
export function MfaPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { challengeId?: string; methods?: string; next?: string };
  const methods = (search.methods ?? "totp").split(",");
  const passkeyOnly = methods.includes("passkey") && !methods.includes("totp") && !methods.includes("recovery");
  const [method, setMethod] = useState<"totp" | "recovery">(methods.includes("totp") ? "totp" : "recovery");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = (search.next && search.next.startsWith("/") ? search.next : "/") as "/";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.auth.mfaVerify({ challengeId: search.challengeId ?? "", method, code: code.replace(/\s+/g, "") });
      authStore.applyAuthResult(r);
      void navigate({ to: next });
    } catch (err) {
      setError((err as ApiRequestError).status === 401 ? "That code is not valid. Codes rotate every 30 seconds." : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const passkey = async () => {
    setBusy(true);
    setError(null);
    try {
      // The login challenge scopes the options to that user's credentials; the
      // id we verify with is the fresh one the server issued alongside them.
      const options = await api.auth.passkeyLoginOptions(search.challengeId);
      const r = await api.auth.passkeyLoginVerify(await getPasskey(options));
      authStore.applyAuthResult(r);
      void navigate({ to: next });
    } catch (err) {
      // The code field is still there to fall back to, so a dismissed sheet
      // needs no message.
      if (!(err instanceof PasskeyCancelledError)) setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (passkeyOnly) {
    return (
      <AuthFrame title="Confirm it's you" lead="Use your passkey to finish signing in.">
        {error && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}
        <button className="btn primary block" type="button" onClick={() => void passkey()} disabled={busy || !passkeysSupported()}>
          {busy ? "Waiting…" : "Use your passkey"}
        </button>
        <div className="auth-links">
          <Link to="/login">Cancel</Link>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Two-factor verification" lead={method === "totp" ? "Enter the 6-digit code from your authenticator app." : "Enter one of your recovery codes."}>
      <form onSubmit={submit} autoComplete="off">
        <Field label={method === "totp" ? "Authenticator code" : "Recovery code"} error={error}>
          {(p) => (
            <input
              {...p}
              className="input"
              inputMode={method === "totp" ? "numeric" : "text"}
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={method === "totp" ? "123 456" : "xxxx-xxxx-xxxx"}
              autoFocus
            />
          )}
        </Field>
        <button className="btn primary block" type="submit" disabled={busy || code.length < 6}>
          {busy ? "Verifying…" : "Verify"}
        </button>
      </form>
      {methods.includes("passkey") && passkeysSupported() && (
        <button className="btn block" style={{ marginTop: 8 }} onClick={() => void passkey()} disabled={busy}>
          Use a passkey instead
        </button>
      )}
      <div className="auth-links">
        {methods.includes("recovery") && (
          <button className="btn ghost sm" type="button" onClick={() => setMethod(method === "totp" ? "recovery" : "totp")}>
            {method === "totp" ? "Use a recovery code" : "Use authenticator code"}
          </button>
        )}
        <Link to="/login">Cancel</Link>
      </div>
    </AuthFrame>
  );
}

// ---------------------------------------------------------------- Invitation
export function InvitePage() {
  const navigate = useNavigate();
  const { token } = useParams({ strict: false }) as { token: string };
  const auth = useAuth();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The invitation carries the email; the person only adds a name and password.
  const [invite, setInvite] = useState<{ email: string; orgName: string } | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  useEffect(() => {
    api.auth.peekInvite(token).then((i) => setInvite({ email: i.email, orgName: i.orgName })).catch((err) => setInviteError((err as ApiRequestError).message));
  }, [token]);

  const accept = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = auth.status === "authed"
        ? await api.auth.acceptInvite(token)
        : await api.auth.signup({ email: invite?.email ?? "", password, name: name.trim() || undefined, inviteToken: token });
      authStore.applyAuthResult(r);
      void navigate({ to: "/" });
    } catch (err) {
      setError((err as ApiRequestError).message);
    } finally {
      setBusy(false);
    }
  };

  const orgLabel = invite?.orgName ? invite.orgName : "this organization";
  if (auth.status === "authed") {
    return (
      <AuthFrame title="Accept invitation" lead={`Join ${orgLabel} as ${auth.me?.user.email}.`}>
        {(error || inviteError) && (
          <div className="error-box" role="alert">
            {error ?? inviteError}
          </div>
        )}
        <button className="btn primary block" onClick={() => void accept()} disabled={busy || !!inviteError}>
          Accept and continue
        </button>
      </AuthFrame>
    );
  }
  if (inviteError) {
    return (
      <AuthFrame title="Invitation not found" lead="This link has expired or was already used. Ask the person who invited you to send a new one.">
        <div className="auth-links">
          <Link to="/login">Sign in</Link>
        </div>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame title={`Join ${orgLabel}`} lead="Create your account to accept, or sign in if you already have one.">
      <form onSubmit={accept} noValidate autoComplete="off">
        <Field label="Email" hint="The address the invitation was sent to.">{(p) => <input {...p} className="input" type="email" value={invite?.email ?? ""} readOnly disabled />}</Field>
        <Field label="Name" hint="Shown to your team, for example on issues assigned to you.">{(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />}</Field>
        <Field label="Password" error={error} hint="At least 12 characters.">
          {(p) => <input {...p} className="input" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} />}
        </Field>
        <button className="btn primary block" type="submit" disabled={busy || !invite || passwordProblems(password) !== null}>
          Accept invitation
        </button>
      </form>
      <div className="auth-links">
        <span className="muted">Have an account?</span>
        <Link to="/login" search={{ next: `/invite/${token}` }}>
          Sign in first
        </Link>
      </div>
    </AuthFrame>
  );
}
